import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useAuth } from "@/hooks/use-auth";
import { getSubdomain } from "@lyder-talk/core";
import {
    initializeOneSignal,
    isOneSignalAvailable,
    OneSignalError,
    resetOneSignalInitialization,
    logoutOneSignal
} from "@lyder-talk/core";

export interface OneSignalErrorType {
    message: string;
    code: string;
}

interface OneSignalContextType {
    error: OneSignalErrorType | null;
    isRetrying: boolean;
    retry: () => Promise<void>;
    dismiss: () => void;
}

const OneSignalContext = createContext<OneSignalContextType | undefined>(undefined);

export const OneSignalProvider = ({ children }: { children: ReactNode }) => {
    const { data: isAuthenticated, isLoading: authLoading } = useAuth();
    const subdomain = getSubdomain();
    const [error, setError] = useState<OneSignalErrorType | null>(null);
    const [isRetrying, setIsRetrying] = useState(false);

    useEffect(() => {
        // Skip OneSignal if not on app subdomain to avoid pollution
        if (subdomain !== 'app') return;

        // Only initialize OneSignal if user is authenticated
        if (authLoading) return;

        if (!isAuthenticated) {
            // Clean up OneSignal state if user logs out
            logoutOneSignal();
            return;
        }

        // Initialize OneSignal when authenticated
        const initOneSignal = async () => {
            // Bounded, not infinite: a blocked CDN must not leave a timer
            // polling for the rest of the session.
            const MAX_ATTEMPTS = 50;
            let attempts = 0;

            // Wait for OneSignal SDK to load
            const checkOneSignal = async () => {
                if (!isOneSignalAvailable() || !window.OneSignal) {
                    if (++attempts < MAX_ATTEMPTS) setTimeout(checkOneSignal, 100);
                    else if (import.meta.env.DEV) console.warn("[OneSignal] SDK did not load");
                    return;
                }

                const appId = import.meta.env.VITE_ONESIGNAL_APP_ID;
                if (!appId) return;

                try {
                    await initializeOneSignal(appId);
                    // Clear error on success
                    setError(null);
                } catch (error) {
                    if (!(error instanceof OneSignalError)) {
                        console.error("OneSignal initialization failed:", error);
                        setError({
                            message: error instanceof Error ? error.message : "Unknown error",
                            code: "UNKNOWN_ERROR",
                        });
                        return;
                    }

                    if (error.code === "SDK_NOT_LOADED") {
                        // SDK not loaded yet, retry a bounded number of times
                        if (++attempts < MAX_ATTEMPTS) setTimeout(checkOneSignal, 1000);
                        return;
                    }

                    // Expected on staging and preview deployments: the OneSignal
                    // dashboard decides which hosts may subscribe, so this is not
                    // a learner-facing failure (it used to paint a red banner on
                    // every page there).
                    if (error.code === "DOMAIN_RESTRICTED") {
                        if (import.meta.env.DEV) {
                            console.warn("OneSignal domain restriction:", error.message);
                        }
                        return;
                    }

                    // Everything else is worth showing with a retry action.
                    setError({ message: error.message, code: error.code });
                    console.error("OneSignal initialization error:", error.message);
                }
            };

            // Start checking after a small delay to ensure script has loaded
            setTimeout(checkOneSignal, 500);
        };

        initOneSignal();
    }, [isAuthenticated, authLoading, subdomain]);

    const retry = async () => {
        setIsRetrying(true);
        setError(null);

        // Reset initialization state
        resetOneSignalInitialization();

        const appId = import.meta.env.VITE_ONESIGNAL_APP_ID;
        if (appId && isOneSignalAvailable() && window.OneSignal) {
            try {
                await initializeOneSignal(appId);
                // Success - error will be cleared by the initialization handler
            } catch (error) {
                // Error will be set by the initialization handler
                if (error instanceof OneSignalError) {
                    setError({
                        message: error.message,
                        code: error.code,
                    });
                } else {
                    setError({
                        message: error instanceof Error ? error.message : "Unknown error",
                        code: "UNKNOWN_ERROR",
                    });
                }
            }
        }

        setIsRetrying(false);

        // Refresh the page after retry
        window.location.reload();
    };

    const dismiss = () => {
        setError(null);
    };

    return (
        <OneSignalContext.Provider value={{ error, isRetrying, retry, dismiss }}>
            {children}
        </OneSignalContext.Provider>
    );
};

export const useOneSignal = () => {
    const context = useContext(OneSignalContext);
    if (context === undefined) {
        throw new Error("useOneSignal must be used within a OneSignalProvider");
    }
    return context;
};
